import { typographyTokens } from '../../theme/typographyTokens';
import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Stack,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  Typography,
  Autocomplete,
  useTheme
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useAccount } from '@azure/msal-react';
import { TaskExtended, TaskPhase } from '../../../shared/types/Task';
import { useGetDepartmentsQuery } from '../../services/departmentSlice/departmentSlice';
import { useGetTasksWithSEPIDQuery } from '../../services/tasksSlice/tasksSlice';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Department } from '../../../shared/types/Department';
import { TaskContext } from './CreateTask';
import AutoComplete from '../AutoComplete/AutoComplete';
import { User } from '../../../shared/types/User';
import { debounce } from 'lodash';
import { useSearchUsersQuery } from '../../services/usersSlice';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#646669',
    color: '#FBFCFC',
    boxShadow: theme.shadows[1],
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledTextField = styled(TextField)({
  '& input': {
    fontSize: typographyTokens.fontSizes.body,
    cursor: 'pointer',
  },
  '& .MuiInputBase-root': {
    paddingRight: '8px',
    cursor: 'pointer',
  },
  '& .Mui-disabled': {
    color: 'rgba(0,0,0,1)',
  },
});

const Placeholder = ({ children, sx }: { children: string, sx?: any }) => {
  const theme = useTheme();
  return <div style={{ color: sx?.color || (theme.palette.mode === 'dark' ? theme.palette.content.white : theme.palette.content.black
  ), fontSize: typographyTokens.fontSizes.body }}>{children}</div>;
};

interface OnChangedTaskProp {
  name: string;
  id: number;
}

const SecondStep = ({ sepID, displayName }: { sepID: number, displayName:any }) => {
  const { task, setTask } = useContext(TaskContext);
  const inputRef = useRef(null);
  const inputPrecedingTaskRef = useRef(null);
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [dependTask, setDependTask] = useState<OnChangedTaskProp>({
    name: '',
    id: 0,
  });
  const { data } = useGetDepartmentsQuery();
  const { data: sepTasks } = useGetTasksWithSEPIDQuery(sepID);
  const [showPrecedingDropDown, setShowPrecedingDropDown] = useState<boolean>(false);
  const [precedingTask, setPrecedingTask] = useState<OnChangedTaskProp>({
    name: '',
    id: 0,
  });


  const [searchTerm, setSearchTerm] = useState<string>('');
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [defaultAssigneeRadio, setDefaultAssigneeRadio]= useState<string>('');
  const { data: searchResults = [], isLoading, isError, error } = useSearchUsersQuery(
    {
      search: searchTerm,
    },
    { skip: searchTerm.trim() === '' }
  );  

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const allTask = useMemo(() => {
    if (sepTasks) {
      return sepTasks
    }
    return [];
  }, [sepTasks]);

  const debouncedSearch = useRef<ReturnType<typeof debounce>>(debounce((term: string) => setSearchTerm(term), 100));
  useEffect(() => {
    debouncedSearch.current(searchTerm);
    return () => debouncedSearch.current.cancel();
  }, [searchTerm]);

  useEffect(() => {
    if (!isLoading && !isError) {
      setUserOptions((prevOptions) => {

        return JSON.stringify(prevOptions) !== JSON.stringify(searchResults) ? searchResults : prevOptions;
      });
    }
  }, [searchResults, isLoading, isError]);

  useEffect(()=>{
    setDefaultAssigneeRadio(task.defaultAssignee.length>12 ? 'Other': task.defaultAssignee)
    
  },[task])

  const account = useAccount();
  const roleList: any = useMemo(() => {
    return account?.idTokenClaims?.roles;
  }, [account]);  

  const departments: Department[] = [];

  if (roleList && roleList.length && data) {
    const superUser = roleList?.includes('AuthSuperUser');
    data.forEach((item: Department) => {
      const adRole = roleList?.find((role: any) => role === item.adAppRole);
      if (adRole || superUser) {
        departments.push(item);
      }
    });
  }

  const mapAndSortTaskByName = (tasks: TaskExtended[]) => {
    return tasks
      .map(e => ({ name: e?.name, id: e?.id }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  return (
    <Stack
      display="flex"
      direction="column"
      alignItems="left"
      spacing={{ xs: 2, md: 4 }}
      sx={{ p: { xs: 2, md: 4 } }}
    >
      <FormControlLabel
        control={
          <Checkbox
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTask({ ...task, review: e.target.checked })
            }
            checked={task.review}
          />
        }
        label="This task requires a review"
        sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}
      />
      <Box>
        <FormLabel
          id="demo-controlled-radio-buttons-group"
          sx={{ color:  isDarkMode ? theme.palette.content.white : theme.palette.content.default }}
        >
          Default Assignee &nbsp;
          <LightTooltip title="The person the task will be assigned to by default. Tasks can reassigned from the task details screen.">
            <i className="fa-solid fa-circle-info"></i>
          </LightTooltip>
        </FormLabel>
        <RadioGroup
          aria-labelledby="demo-controlled-radio-buttons-group"
          name="controlled-radio-buttons-group"
          value={defaultAssigneeRadio}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDefaultAssigneeRadio(e.target.value)
            setTask({ ...task, defaultAssignee: e.target.value })
          } }>
          <FormControlLabel
            value="requestor"
            control={<Radio />}
            label="Requestor"
          />
          <FormControlLabel
            value="stakeholder"
            control={<Radio />}
            label="Stakeholder"
          />
          <FormControlLabel
            value="Other"
            control={<Radio />}
            label="Other"
          />
        </RadioGroup>
        {defaultAssigneeRadio && defaultAssigneeRadio=== 'Other' && (
          <React.Fragment>
            <Box sx={{mt:2}}>
            <Autocomplete
              options={userOptions}
              getOptionLabel={(user) => user.displayName || user.email || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                if(value?.id){
                  setTask({ ...task, 'defaultAssignee':value.id, 'departtmetID':3})
                }
                if (value?.email) {
                  setErrorMessage('');
                } else {
                  setErrorMessage('Enter a valid user ID');
                }
              }}
              inputValue={searchTerm}
              onInputChange={(_, newInputValue) => debouncedSearch.current(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label= {displayName ? displayName :   "Search and Select Users"} 
                  sx={{
                    borderColor: task.defaultAssignee === 'Other' && searchTerm.trim() === '' ? 'red' : 'default',
                    '& input': {  
                      fontSize: typographyTokens.fontSizes.body,                    
                      cursor: 'pointer',
                      color: task.defaultAssignee === 'Other' && searchTerm.trim() === '' ? 'red' : 'default',
                    },
                  }}
                />
              )}
              />
              {!displayName  && searchTerm.trim() === '' && (
                <Typography sx={{ color: 'red', fontSize: typographyTokens.fontSizes.caption, mt: 1 }}>
                  Enter a valid user ID
                </Typography>
              )}
              </Box>
            </React.Fragment>
          )}
      </Box>
      <Box>
        <InputLabel sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>Department</InputLabel>
        <Select
          size="small"
          id="demo-simple-select-helper"
          value={task.departmentID}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) =>
            setTask({ ...task, departmentID: event.target.value })
          }
          sx={{ width: '100%', textTransform: 'capitalize' }}
        >
          <MenuItem value={'0'}>
            <Placeholder sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.prime.granite }}>Choose a department from the dropdown</Placeholder>
          </MenuItem>
          {departments?.map((e, i) => (
            <MenuItem key={i} value={e.id} sx={{ textTransform: 'capitalize' }}>
              {e.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box width={{ xs: '100%' }}>
        <InputLabel sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>Phase</InputLabel>
        <Select
          size="small"
          id="demo-simple-select-helper"
          value={task.phase}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) =>
            setTask({ ...task, phase: event.target.value })
          }
          sx={{ width: '100%', textTransform: 'capitalize' }}
        >
          <MenuItem value="">
            <Placeholder sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.prime.granite }}>Choose a phase from the dropdown</Placeholder>
          </MenuItem>
          {Object.values(TaskPhase).map((e, i) => (
            <MenuItem key={i} value={e} sx={{ textTransform: 'capitalize' }}>
              {e}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box width={{ xs: '100%' }}>
        <InputLabel sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
          Preceding Task ( Optional )
        </InputLabel>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StyledTextField
            ref={inputPrecedingTaskRef}
            size="small"
            value={
              precedingTask.name ||
              'Choose the task that should be completed prior to your new tasks being set to "To-do"'
            }
            sx={{ width: { xs: '100%' } }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            }}
            onClick={() => setShowPrecedingDropDown(!showPrecedingDropDown)}
          ></StyledTextField>
          {precedingTask && precedingTask.id !== 0 && (
            <Box
              component="span"
              sx={{ cursor: 'pointer', ml: 1, color: isDarkMode ? theme.palette.content.white : theme.palette.prime.granite, fontSize: typographyTokens.fontSizes.h6 }}
              onClick={() => {
                setPrecedingTask({ name: '', id: 0 });
                setTask({ ...task, precedingTaskID: 0 });
              }}
              aria-label="Clear selected preceding task"
              title="Clear selected preceding task"
            >
              &#10005;
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AutoComplete
            data={allTask
              .filter((t) => t.id !== dependTask.id)
              .map((e) => ({ name: e?.name, id: e?.id }))
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            }
            type="precedingTask"
            show={showPrecedingDropDown}
            anchor={inputPrecedingTaskRef.current}
            onClose={() => setShowPrecedingDropDown(false)}
            onChange={(e) => {
              setPrecedingTask(e);
              setTask({ ...task, precedingTaskID: e.id });
              setShowPrecedingDropDown(false);
            }}
            showConfirmButton={false}
          />
        </Box>
        <Box
          sx={{
            font: 'normal normal normal 11px/22px Open Sans',
            letterSpacing: '0px',
            color: theme.palette.prime.granite,
          }}
        >The chosen task needs to be completed prior to the task you are creating
        </Box>
      </Box>
      <Box component='div' sx={{ mt: 2, justifyItems: 'center' }}>
        <Box>
          <ArrowDownwardIcon />
        </Box>
        <Box sx={{ borderStyle: 'double', marginBottom: '6px', padding: '7px 14px', backgroundColor: isDarkMode ? theme.palette.background.default : 'cornsilk', color: isDarkMode ? theme.palette.content.white : theme.palette.content.black, fontSize: typographyTokens.fontSizes.body }}>
          {task?.name + ' ( ' + task?.shortName + ' )'}
        </Box>
        <Box>
          <ArrowDownwardIcon />
        </Box>
      </Box>
      <Box component='div' width={{ xs: '100%' }}>
        <InputLabel sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
          Following Task ( Optional )
        </InputLabel>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StyledTextField
            ref={inputRef}
            size="small"
            value={
              dependTask.name ||
              'Choose the task that should wait for this new task to complete'
            }
            sx={{ width: { xs: '100%' } }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            }}
            onClick={() => setShowDropDown(!showDropDown)}
          ></StyledTextField>
          {dependTask && dependTask.id !== 0 && (
            <Box
              component="span"
              sx={{ cursor: 'pointer', ml: 1, color: isDarkMode ? theme.palette.content.white : theme.palette.prime.granite, fontSize: typographyTokens.fontSizes.h6 }}
              onClick={() => {
                setDependTask({ name: '', id: 0 });
                setTask({ ...task, dependentTaskID: 0 });
              }}
              aria-label="Clear selected following task"
              title="Clear selected following task"
            >
              &#10005;
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AutoComplete
            data={allTask
              .filter((t) => t.id !== precedingTask.id)
              .map((e) => ({ name: e?.name, id: e?.id }))
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            }
            type="dependentTask"
            show={showDropDown}
            anchor={inputRef.current}
            onClose={() => setShowDropDown(false)}
            onChange={(e) => {
              setDependTask(e);
              setTask({ ...task, dependentTaskID: e.id });
              setShowDropDown(false);
            }}
            showConfirmButton={false}
          />
        </Box>
        <Box
          sx={{
            font: 'normal normal normal 11px/22px Open Sans',
            letterSpacing: '0px',
            color: isDarkMode ? theme.palette.content.white : theme.palette.prime.granite,
          }}
        >
          The chosen task will remain in locked status until the new task you are creating is complete
        </Box>
      </Box>
    </Stack>
  );
};

export default SecondStep;
